import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  GraphQLClient,
  fetchRepossessionsWithPreviousV2,
  fetchSpottedVehiclesForBranch,
  fetchSpottedWithPreviousV2,
  fetchPeopleWorkingCount,
  fetchPeopleWorkingData,
  fetchNewRepossessionsDetails,
} from '@rapid-recovery-agency-inc/insightt-shared';

import { InsighttNotification } from '../../helpers/insightt-notification';
import {
  FetchPeopleWorkingCountActionInput,
  FetchPeopleWorkingCountActionResponse,
  FetchPeopleWorkingDataInput,
  FetchPeopleWorkingDataResponse,
  GetReposInput,
  GetReposResponse,
  GetReposWithPreviousInput,
  GetReposWithPreviousResponse,
  GetSpottedInput,
  GetSpottedResponse,
  GetSpottedVehiclesWithPreviousInput,
  GetSpottedVehiclesWithPreviousResponse,
} from './types';
import {
  FETCH_PEOPLE_WORKING,
  FETCH_PEOPLE_WORKING_COUNT,
  GET_REPOS,
  GET_REPOS_WITH_PREVIOUS,
  GET_SPOTTED,
  GET_SPOTTED_VEHICLES_WITH_PREVIOUS,
} from './constants';
import { client } from '../../shared/apollo';

const apolloClient = client as GraphQLClient;

export const getRepos = createAsyncThunk<GetReposResponse, GetReposInput, { rejectValue: Error }>(
  GET_REPOS,
  async ({ startDate, endDate, branchId }, { fulfillWithValue, rejectWithValue }) => {
    try {
      // const { rDNCases: repossessions } = await fetchRepossessionsForBranch(
      //   apolloClient,
      //   branchId,
      //   startDate,
      //   endDate,
      // );

      const repossessions = await fetchNewRepossessionsDetails(apolloClient, {
        startDateTime: startDate.toISOString(),
        endDateTime: endDate.toISOString(),
        branchId,
      });

      return fulfillWithValue({ repossessions });
    } catch (err) {
      InsighttNotification('error', 'Get repossessions error', err);
      return rejectWithValue(new Error(String(err)));
    }
  },
);

export const getReposWithPrevious = createAsyncThunk<
  GetReposWithPreviousResponse,
  GetReposWithPreviousInput,
  { rejectValue: Error }
>(
  GET_REPOS_WITH_PREVIOUS,
  async (
    { startDate, endDate, branchId, momentUnit, timezone, fetchPolicy = 'cache-first' },
    { fulfillWithValue, rejectWithValue },
  ) => {
    try {
      const { repossessionsCount } = await fetchRepossessionsWithPreviousV2(apolloClient, {
        startDate,
        endDate,
        branchId,
        momentUnit,
        timezone,
        fetchPolicy,
      });

      return fulfillWithValue({ repossessionsCount });
    } catch (err) {
      InsighttNotification('error', 'Get repossessions error', err);
      return rejectWithValue(new Error(String(err)));
    }
  },
);

export const getSpottedVehiclesWithPrevious = createAsyncThunk<
  GetSpottedVehiclesWithPreviousResponse,
  GetSpottedVehiclesWithPreviousInput,
  { rejectValue: Error }
>(
  GET_SPOTTED_VEHICLES_WITH_PREVIOUS,
  async ({ startDate, endDate, branchId, momentUnit, timezone }, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { spottedVehiclesCount, unitsPendingRepoCount } = await fetchSpottedWithPreviousV2(apolloClient, {
        startDate,
        endDate,
        branchId,
        momentUnit,
        timezone,
      });

      return fulfillWithValue({
        spottedVehiclesCount,
        unitsPendingRepoCount,
      });
    } catch (err) {
      InsighttNotification('error', 'Get repossessions error', err);
      return rejectWithValue(new Error(String(err)));
    }
  },
);

export const getSpotted = createAsyncThunk<GetSpottedResponse, GetSpottedInput, { rejectValue: Error }>(
  GET_SPOTTED,
  async ({ startDate, endDate, branchId }, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { rDNCases: spottedVehicles, unitsPendingRepossession: unitsPendingRepo } =
        await fetchSpottedVehiclesForBranch(apolloClient, branchId, startDate, endDate);

      return fulfillWithValue({ spottedVehicles, unitsPendingRepo });
    } catch (err) {
      InsighttNotification('error', 'Get spotted vehicles error', err);
      return rejectWithValue(new Error(String(err)));
    }
  },
);

let latestPeopleWorkingDataRequestId = 0;
let latestPeopleWorkingCountRequestId = 0;

export const fetchPeopleWorkingDataAction = createAsyncThunk<
  FetchPeopleWorkingDataResponse,
  FetchPeopleWorkingDataInput,
  { rejectValue: Error }
>(
  FETCH_PEOPLE_WORKING,
  async (
    { startDateTime, endDateTime, branchId, fetchPolicy = 'cache-first', isSilent },
    { fulfillWithValue, rejectWithValue },
  ) => {
    const dataRequestId = ++latestPeopleWorkingDataRequestId;
    try {
      const { peopleWorking } = await fetchPeopleWorkingData(apolloClient, {
        startDateTime,
        endDateTime,
        branchId,
        fetchPolicy,
      });

      if (dataRequestId !== latestPeopleWorkingDataRequestId) {
        return rejectWithValue(new Error('Stale request'));
      }

      return fulfillWithValue({ peopleWorking });
    } catch (err) {
      if (dataRequestId === latestPeopleWorkingDataRequestId && !isSilent) {
        InsighttNotification('error', 'Fetch People Working Data error', err);
      }
      return rejectWithValue(new Error(String(err)));
    }
  },
);

export const fetchPeopleWorkingCountAction = createAsyncThunk<
  FetchPeopleWorkingCountActionResponse,
  FetchPeopleWorkingCountActionInput,
  { rejectValue: Error }
>(
  FETCH_PEOPLE_WORKING_COUNT,
  async (
    { startDateTime, endDateTime, timezone, momentUnit, branchId, fetchPolicy = 'cache-first', isSilent },
    { fulfillWithValue, rejectWithValue, dispatch },
  ) => {
    const countRequestId = ++latestPeopleWorkingCountRequestId;
    let isRosterError = false;
    try {
      const { peopleWorkingCount } = await fetchPeopleWorkingCount(apolloClient, {
        startDateTime,
        endDateTime,
        timezone,
        momentUnit,
        branchId,
        fetchPolicy,
      });

      if (countRequestId !== latestPeopleWorkingCountRequestId) {
        return rejectWithValue(new Error('Stale request'));
      }

      try {
        await dispatch(
          fetchPeopleWorkingDataAction({
            startDateTime,
            endDateTime,
            branchId,
            fetchPolicy,
            isSilent,
          }),
        ).unwrap();
      } catch (rosterErr) {
        isRosterError = true;
        throw rosterErr;
      }

      if (countRequestId !== latestPeopleWorkingCountRequestId) {
        return rejectWithValue(new Error('Stale request'));
      }

      return fulfillWithValue({ peopleWorkingCount });
    } catch (err) {
      if (countRequestId === latestPeopleWorkingCountRequestId && !isSilent && !isRosterError) {
        InsighttNotification('error', 'Fetch People Working Count error', err);
      }
      return rejectWithValue(new Error(String(err)));
    }
  },
);
